<template>
  <div class="tiptap-wrapper" :class="{ 'is-focused': editor?.isFocused }">

    <!-- Toolbar -->
    <div class="tiptap-toolbar" @mousedown.prevent>

      <!-- Text format -->
      <button
        class="tb-btn" :class="{ active: editor?.isActive('bold') }"
        @click="editor.chain().focus().toggleBold().run()" title="Bold"
      ><b>B</b></button>

      <button
        class="tb-btn" :class="{ active: editor?.isActive('italic') }"
        @click="editor.chain().focus().toggleItalic().run()" title="Italic"
      ><i>I</i></button>

      <button
        class="tb-btn" :class="{ active: editor?.isActive('underline') }"
        @click="editor.chain().focus().toggleUnderline().run()" title="Underline"
      ><u>U</u></button>

      <button
        class="tb-btn" :class="{ active: editor?.isActive('strike') }"
        @click="editor.chain().focus().toggleStrike().run()" title="Strikethrough"
      ><s>S</s></button>

      <div class="tb-sep" />

      <!-- Headings -->
      <button
        v-for="level in [1, 2, 3]" :key="level"
        class="tb-btn" :class="{ active: editor?.isActive('heading', { level }) }"
        @click="editor.chain().focus().toggleHeading({ level }).run()"
        :title="`Heading ${level}`"
      >H{{ level }}</button>

      <div class="tb-sep" />

      <!-- Lists -->
      <button
        class="tb-btn" :class="{ active: editor?.isActive('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()" title="Bullet list"
      >≡</button>

      <button
        class="tb-btn" :class="{ active: editor?.isActive('orderedList') }"
        @click="editor.chain().focus().toggleOrderedList().run()" title="Numbered list"
      >1.</button>

      <div class="tb-sep" />

      <!-- Alignment -->
      <button
        class="tb-btn" :class="{ active: editor?.isActive({ textAlign: 'left' }) }"
        @click="editor.chain().focus().setTextAlign('left').run()" title="Align left"
      >⇤</button>

      <button
        class="tb-btn" :class="{ active: editor?.isActive({ textAlign: 'center' }) }"
        @click="editor.chain().focus().setTextAlign('center').run()" title="Center"
      >⇔</button>

      <button
        class="tb-btn" :class="{ active: editor?.isActive({ textAlign: 'right' }) }"
        @click="editor.chain().focus().setTextAlign('right').run()" title="Align right"
      >⇥</button>

      <div class="tb-sep" />

      <!-- Text color -->
      <label class="tb-btn color-btn" title="Text color">
        <span class="color-icon">A</span>
        <span class="color-bar" :style="{ background: currentTextColor }" />
        <input
          type="color"
          :value="currentTextColor"
          @input="e => editor.chain().focus().setColor(e.target.value).run()"
          class="color-input"
        />
      </label>

      <!-- Highlight color -->
      <label class="tb-btn color-btn" :class="{ active: editor?.isActive('highlight') }" title="Highlight">
        <span class="color-icon" style="font-size:0.75rem">▐H</span>
        <span class="color-bar" :style="{ background: currentHighlight }" />
        <input
          type="color"
          :value="currentHighlight"
          @input="e => editor.chain().focus().setHighlight({ color: e.target.value }).run()"
          class="color-input"
        />
      </label>

      <!-- Clear color -->
      <button
        class="tb-btn"
        @click="clearColors()" title="Remove color"
        style="font-size:0.7rem; color:#6b7280"
      >⊘</button>

      <div class="tb-sep" />

      <!-- Font size -->
      <select class="tb-select" @change="e => setFontSize(e.target.value)" title="Font size">
        <option value="">Size</option>
        <option v-for="s in fontSizes" :key="s" :value="s" :selected="currentFontSize === s">{{ s }}</option>
      </select>

      <div class="tb-sep" />

      <!-- Link -->
      <button
        class="tb-btn" :class="{ active: editor?.isActive('link') }"
        @click="setLink()" title="Link"
      >🔗</button>

      <!-- Image upload -->
      <button
        class="tb-btn" :class="{ 'is-uploading': uploading }"
        @click="triggerImageUpload()" title="Insert image"
        :disabled="uploading"
      >
        <span v-if="uploading" class="upload-spin">⏳</span>
        <span v-else>🖼️</span>
      </button>
      <input ref="fileInput" type="file" accept="image/*" hidden @change="onFileSelected" />

    </div>

    <!-- Editor content -->
    <div class="editor-row">
      <editor-content class="tiptap-content" :editor="editor" />

      <!-- Variables slot -->
      <slot name="variables" />
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { TextStyle, Color } from '@tiptap/extension-text-style'
import { Highlight } from '@tiptap/extension-highlight'
import { mergeAttributes } from '@tiptap/core'
import api from '@/services/api'

// Override Highlight to render <span> instead of <mark> for Outlook compatibility
const EmailHighlight = Highlight.extend({
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
  },
})

// ── Props & emits ──────────────────────────────────────────────────────────────
const props = defineProps({
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: 'Write your email...' },
})

const emit = defineEmits(['update:modelValue'])

// ── State ──────────────────────────────────────────────────────────────────────
const fileInput = ref(null)
const uploading = ref(false)

const fontSizes = ['10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px']

// ── Editor setup ───────────────────────────────────────────────────────────────
const editor = useEditor({
  content: props.modelValue,
  extensions: [
    StarterKit,
    Underline,
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: { rel: 'noopener noreferrer' },
    }),
    Image.configure({
      inline: false,
      allowBase64: false,
      HTMLAttributes: { class: 'email-img' },
    }),
    Placeholder.configure({ placeholder: props.placeholder }),
    TextStyle,
    Color,
    EmailHighlight.configure({ multicolor: true }),
  ],
  onUpdate: ({ editor }) => {
    emit('update:modelValue', editor.getHTML())
  },
})

// ── Computed color state ───────────────────────────────────────────────────────
const currentTextColor = computed(() =>
  editor.value?.getAttributes('textStyle').color ?? '#000000'
)

const currentHighlight = computed(() =>
  editor.value?.getAttributes('highlight').color ?? '#ffff00'
)

const currentFontSize = computed(() =>
  editor.value?.getAttributes('textStyle').fontSize ?? ''
)

// ── Color helpers ──────────────────────────────────────────────────────────────
const clearColors = () => {
  editor.value?.chain().focus().unsetColor().unsetHighlight().run()
}

const setFontSize = (size) => {
  if (!size) {
    editor.value?.chain().focus().unsetMark('textStyle').run()
  } else {
    editor.value?.chain().focus().setMark('textStyle', { fontSize: size }).run()
  }
}

// Sync when parent changes content (e.g. language switch)
watch(() => props.modelValue, (val) => {
  if (!editor.value) return
  const current = editor.value.getHTML()
  if (val !== current) {
    editor.value.commands.setContent(val ?? '', false)
  }
})

onBeforeUnmount(() => editor.value?.destroy())

// ── Variable insertion (called by parent via template ref) ─────────────────────
const insertText = (text) => {
  editor.value?.chain().focus().insertContent(text).run()
}

// ── Link ───────────────────────────────────────────────────────────────────────
const setLink = () => {
  const prev = editor.value?.getAttributes('link').href ?? ''
  const url = window.prompt('Link URL:', prev)
  if (url === null) return
  if (url === '') {
    editor.value?.chain().focus().extendMarkRange('link').unsetLink().run()
    return
  }
  editor.value?.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

// ── Image upload → MinIO via existing /api/media endpoint ─────────────────────
const triggerImageUpload = () => fileInput.value?.click()

const onFileSelected = async (e) => {
  const file = e.target.files?.[0]
  if (!file) return

  e.target.value = ''

  uploading.value = true
  try {
    const formData = new FormData()
    formData.append('media', file)
    formData.append('file_name', file.name)
    formData.append('file_type', 'IMAGE')

    const { data } = await api.post('/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

    const url = data.file_link
    if (url) {
      editor.value?.chain().focus().setImage({ src: url, alt: data.file_name ?? '' }).run()
    }
  } catch (err) {
    console.error('Image upload failed:', err)
    alert('Image upload failed. Please try again.')
  } finally {
    uploading.value = false
  }
}

// Expose insertText for parent to call via template ref
defineExpose({ insertText })
</script>

<style scoped>
.tiptap-wrapper {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  overflow: hidden;
  transition: border-color 0.15s;
}

.tiptap-wrapper.is-focused {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

/* ── Toolbar ─────────────────────────────────────────────────────────────────── */
.tiptap-toolbar {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0.4rem 0.5rem;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  flex-wrap: wrap;
}

.tb-btn {
  min-width: 28px;
  height: 28px;
  padding: 0 0.35rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.1s;
}

.tb-btn:hover:not(:disabled) {
  background: #e5e7eb;
  border-color: #d1d5db;
}

.tb-btn.active {
  background: #dbeafe;
  border-color: #93c5fd;
  color: #1d4ed8;
}

.tb-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.tb-btn.is-uploading { opacity: 0.6; }

.tb-sep {
  width: 1px;
  height: 20px;
  background: #e5e7eb;
  margin: 0 4px;
}

/* ── Color picker buttons ────────────────────────────────────────────────────── */
.color-btn {
  position: relative;
  flex-direction: column;
  gap: 1px;
  padding-bottom: 2px;
  overflow: hidden;
}

.color-icon {
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1;
}

.color-bar {
  width: 18px;
  height: 3px;
  border-radius: 1px;
}

.color-input {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  cursor: pointer;
  padding: 0;
  border: none;
}

/* ── Font size select ────────────────────────────────────────────────────────── */
.tb-select {
  height: 28px;
  padding: 0 0.25rem;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background: transparent;
  font-size: 0.8rem;
  color: #374151;
  cursor: pointer;
}

.tb-select:focus { outline: none; border-color: #3b82f6; }

/* ── Upload spin ─────────────────────────────────────────────────────────────── */
.upload-spin { animation: spin 1s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Editor area ─────────────────────────────────────────────────────────────── */
.editor-row {
  display: flex;
  min-height: 220px;
}

.tiptap-content {
  flex: 1;
  overflow-y: auto;
}

/* ProseMirror root element */
:deep(.ProseMirror) {
  padding: 0.75rem 1rem;
  min-height: 220px;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #1f2937;
  outline: none;
}

:deep(.ProseMirror p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  color: #9ca3af;
  pointer-events: none;
  float: left;
  height: 0;
}

:deep(.ProseMirror h1) { font-size: 1.5rem; font-weight: 700; margin: 0.75rem 0 0.25rem; }
:deep(.ProseMirror h2) { font-size: 1.25rem; font-weight: 700; margin: 0.6rem 0 0.2rem; }
:deep(.ProseMirror h3) { font-size: 1.1rem;  font-weight: 600; margin: 0.5rem 0 0.15rem; }
:deep(.ProseMirror ul)  { padding-left: 1.5rem; list-style: disc; }
:deep(.ProseMirror ol)  { padding-left: 1.5rem; list-style: decimal; }
:deep(.ProseMirror a)   { color: #3b82f6; text-decoration: underline; }
:deep(.ProseMirror mark) { border-radius: 2px; padding: 0 2px; }

:deep(.ProseMirror .email-img) {
  max-width: 100%;
  height: auto;
  border-radius: 0.25rem;
  margin: 0.5rem 0;
  display: block;
}
</style>
